import { Container, Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';
import { BaseScene } from '../../scenes/BaseScene.js';
import { PipelineBoard } from './PipelineBoard.js';
import { PipelineTrain } from './PipelineTrain.js';
import { GameOverOverlay } from '../../ui/GameOverOverlay.js';
import { LevelCompleteOverlay } from '../../ui/LevelCompleteOverlay.js';
import { createThemeBackground } from '../../ui/createThemeBackground.js';
import { ScoreSubmitOverlay } from '../../ui/ScoreSubmitOverlay.js';
import { createHudPanel, createCountdownLabel } from '../../ui/shared.js';
import { SceneChrome } from '../../ui/SceneChrome.js';
import { partyMode } from '../../ui/Confetti.js';
import { t } from '../../config/i18n.js';
import {
    PIPELINE_CONFIG, TILE_DEFS, TILE_WEIGHTS,
    rotateConnections, drawTileGraphics, computeGrid,
} from './pipelineConfig.js';
import { FilterPipeline } from '../../systems/GameFilters.js';

const {
    COUNTDOWN_SECONDS, QUEUE_SIZE,
    TRAIN_BASE_SPEED, TRAIN_SPEED_INCREMENT, TRAIN_MAX_SPEED,
    COLOR_TILE_BG, STATION_COUNT_BASE, STATION_COUNT_PER_LEVEL,
    STATION_COUNT_CAP, END_BONUS, STATION_POINTS,
} = PIPELINE_CONFIG;

const HUD_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 28, fontWeight: 'bold', fill: '#ffffff',
});

const MSG_STYLE = new TextStyle({
    fontFamily: 'Arial', fontSize: 48, fontWeight: 'bold', fill: '#ef4444', align: 'center',
});

export class PipelineScene extends BaseScene {
    _grid = { cols: 0, rows: 0, cellSize: 0, originX: 0, originY: 0 };
    _board = null;
    _train = null;

    _worldContainer = null;
    _hudContainer = null;
    _themeBg = null;

    /** @type {{ type: string, rotation: number }[]} */
    _queue = [];
    _queueGfx = [];

    _trainStarted = false;
    _countdownTimer = 0;
    _gameOver = false;
    _levelComplete = false;

    _level = 1;
    _totalScore = 0;
    _totalStations = 0;

    /** @type {LevelCompleteOverlay|null} */
    _overlay = null;
    _gameOverOverlay = null;
    /** @type {ScoreSubmitOverlay|null} */
    _scoreOverlay = null;

    /** @type {SceneChrome|null} */
    _chrome = null;
    _paused = false;

    _hudText = null;
    _countdownText = null;
    _messageText = null;
    _onTap = null;
    _gridTapHandler = null;

    // ── lifecycle ───────────────────────────────────────

    init(game) {
        super.init(game);

        this._trainStarted = false;
        this._countdownTimer = 0;
        this._gameOver = false;
        this._levelComplete = false;
        this._overlay = null;
        this._paused = false;

        this._grid = computeGrid(game.width, game.height);

        this._createThemeBackground();
        this._worldContainer = new Container();
        this._hudContainer = new Container();
        this.container.addChild(this._worldContainer);
        this.container.addChild(this._hudContainer);

        this._createBoard();
        this._createTrain();
        this._queue = [];
        this._fillQueue();
        this._renderQueue();
        this._createHud();
        this._chrome = new SceneChrome({
            game: this.game,
            host: this._hudContainer,
            overlayHost: this.container,
            gameId: 'pipeline',
            getHelp: () => this._buildHelp(),
            onPause: () => { this._paused = true; },
            onResume: () => { this._paused = false; },
        });
        this._chrome.attach();
        this._bindInput();

        // Cross-game post-process filter stack (CRT, Old Film, Pixelate,
        // Tilt-Shift, God Rays). No-op when every shared filter toggle
        // is OFF (the default).
        this._filterFx = new FilterPipeline(this.game, this.container);
    }

    update(delta) {
        if (this._levelComplete) {
            if (this._overlay) this._overlay.update(delta);
            return;
        }

        if (this._gameOver) {
            if (this._gameOverOverlay) this._gameOverOverlay.update(delta);
            return;
        }
        if (this._paused) return;

        if (!this._trainStarted) {
            this._countdownTimer += delta / 60;
            const remaining = Math.ceil(COUNTDOWN_SECONDS - this._countdownTimer);

            if (remaining > 0) {
                this._countdownText.text = String(remaining);
                this._countdownText.visible = true;
            } else if (this._countdownTimer < COUNTDOWN_SECONDS + 0.5) {
                this._countdownText.text = t('common.go');
            } else {
                this._countdownText.visible = false;
                this._startTrain();
            }
            return;
        }

        const result = this._train.update(delta, this._board);
        this._train.render();

        if (result.reachedEnd) {
            this._onLevelComplete();
            return;
        }

        if (!result.alive) {
            this._setGameOver();
            return;
        }

        this._updateHud();

        if (this._filterFx) {
            // Tilt-Shift: keep the in-focus disc on the train head.
            // Once the train starts, `train.gfx` is pinned to (0,0) and
            // the cars draw in board-local coords inside it, so we
            // can't read `gfx.x/.y`. Snap to the current grid cell
            // (`_col`, `_row`) instead — close enough for focus
            // tracking and never drifts off the train.
            if (this._train && this._board?.container && this._grid) {
                const cs = this._grid.cellSize;
                const sx = this._board.container.x + this._train._col * cs + cs / 2;
                const sy = this._board.container.y + this._train._row * cs + cs / 2;
                this._filterFx.setFocus(sx / this.game.width, sy / this.game.height);
            }
            this._filterFx.update(delta);
        }
    }

    destroy() {
        const input = this.game?.input;
        if (input && this._onTap) input.off('tap', this._onTap);

        const hitArea = this._board?.getHitArea();
        if (hitArea && this._gridTapHandler) hitArea.off('pointertap', this._gridTapHandler);

        for (const g of this._queueGfx) {
            if (g.parent) g.parent.removeChild(g);
            g.destroy();
        }
        this._queueGfx = [];

        if (this._filterFx) { this._filterFx.destroy(); this._filterFx = null; }
        if (this._chrome) { this._chrome.destroy(); this._chrome = null; }
        if (this._overlay) { this._overlay.destroy(); this._overlay = null; }
        if (this._gameOverOverlay) { this._gameOverOverlay.destroy(); this._gameOverOverlay = null; }
        if (this._scoreOverlay) { this._scoreOverlay.destroy(); this._scoreOverlay = null; }
        if (this._board) this._board.destroy();
        if (this._train) this._train.destroy();
        this._onTap = null;
        this._gridTapHandler = null;
        this._paused = false;
        super.destroy();
    }

    // ── setup ──────────────────────────────────────────

    _createBoard() {
        const { cols, rows, cellSize, originX, originY } = this._grid;
        this._board = new PipelineBoard(cols, rows, cellSize, this.game.theme);

        const startCol = 0;
        const startRow = Math.floor(rows / 2);

        const { endCol, endRow } = this._pickEndPosition(cols, rows, startCol, startRow);
        const stations = this._pickStationPositions(cols, rows, startCol, startRow, endCol, endRow);
        this._totalStations = stations.length;

        this._board.init(startCol, startRow, endCol, endRow, stations);

        this._worldContainer.addChild(this._board.container);
        this._board.container.x = originX;
        this._board.container.y = originY;
    }

    _createThemeBackground() {
        this._themeBg = createThemeBackground(this.game, 'pipeline.background');
        if (this._themeBg) {
            this.container.addChild(this._themeBg);
        }
    }

    _pickEndPosition(cols, rows, startCol, startRow) {
        const minDist = Math.floor((cols + rows) * 0.4);
        for (let att = 0; att < 100; att++) {
            const c = Math.floor(Math.random() * (cols - 1));
            const r = Math.floor(Math.random() * (rows - 1));
            if (c + 1 >= cols || r + 1 >= rows) continue;
            const dist = Math.abs(c - startCol) + Math.abs(r - startRow);
            if (dist >= minDist) return { endCol: c, endRow: r };
        }
        return { endCol: cols - 2, endRow: Math.max(0, Math.floor(rows / 2) - 1) };
    }

    _pickStationPositions(cols, rows, startCol, startRow, endCol, endRow) {
        const count = Math.min(
            STATION_COUNT_CAP,
            STATION_COUNT_BASE + (this._level - 1) * STATION_COUNT_PER_LEVEL,
        );
        const stations = [];
        const used = new Set([`${startCol},${startRow}`]);
        for (let dc = 0; dc < 2; dc++) {
            for (let dr = 0; dr < 2; dr++) {
                used.add(`${endCol + dc},${endRow + dr}`);
            }
        }
        const margin = 2;

        for (let n = 0; n < count; n++) {
            for (let att = 0; att < 60; att++) {
                const c = Math.floor(Math.random() * (cols - margin * 2)) + margin;
                const r = Math.floor(Math.random() * (rows - margin * 2)) + margin;
                const key = `${c},${r}`;
                if (used.has(key)) continue;
                const distStart = Math.abs(c - startCol) + Math.abs(r - startRow);
                const distEnd = Math.min(
                    Math.abs(c - endCol) + Math.abs(r - endRow),
                    Math.abs(c - (endCol + 1)) + Math.abs(r - (endRow + 1)),
                );
                if (distStart < 3 || distEnd < 3) continue;
                used.add(key);
                stations.push({ col: c, row: r });
                break;
            }
        }
        return stations;
    }

    _createTrain() {
        const { cellSize } = this._grid;
        this._train = new PipelineTrain(cellSize, this.game.theme);
        const speedMultiplier = 1 + (this._level - 1) * 0.08;
        this._train.init(
            this._board.startCol, this._board.startRow,
            TRAIN_BASE_SPEED * speedMultiplier,
            TRAIN_MAX_SPEED * speedMultiplier,
            TRAIN_SPEED_INCREMENT * speedMultiplier,
        );
        this._train.gfx.visible = true;
        this._train.render();
        this._board.container.addChild(this._train.gfx);
    }

    _createHud() {
        const hudPanel = createHudPanel(this.game, 'pipeline', 430, 84);
        if (hudPanel) this._hudContainer.addChild(hudPanel);

        this._hudText = new Text({ text: '', style: HUD_STYLE });
        this._hudText.x = 20;
        this._hudText.y = 12;
        this._hudContainer.addChild(this._hudText);
        this._updateHud();

        this._countdownText = createCountdownLabel(this.game);
        this._hudContainer.addChild(this._countdownText);

        this._messageText = new Text({ text: '', style: MSG_STYLE });
        this._messageText.anchor.set(0.5);
        this._messageText.x = this.game.width / 2;
        this._messageText.y = this.game.height / 2;
        this._messageText.visible = false;
        this._hudContainer.addChild(this._messageText);
    }

    _buildHelp() {
        return {
            title: t('pipeline.help_title'),
            sections: [
                { heading: t('help.goal'), color: 0xfbbf24,
                  body: t('pipeline.help_goal') },
                { heading: t('help.controls'), color: 0xfbbf24,
                  body: t('pipeline.help_controls') },
                { heading: t('help.tile_queue'), color: 0x94a3b8,
                  body: t('pipeline.help_queue') },
                { heading: t('help.countdown'), color: 0xfbbf24,
                  body: t('pipeline.help_countdown') },
                { heading: t('help.train'), color: 0xa855f7,
                  body: t('pipeline.help_train') },
                { heading: t('help.end_building'), color: 0xef4444,
                  body: t('pipeline.help_end') },
                { heading: t('help.stations'), color: 0x3b82f6,
                  body: t('pipeline.help_stations') },
                { heading: t('help.obstacles'), color: 0x64748b,
                  body: t('pipeline.help_obstacles') },
                { heading: t('help.scoring'), color: 0x22c55e,
                  body: t('pipeline.help_scoring') },
                { heading: t('help.levels'), color: 0x22c55e,
                  body: t('pipeline.help_levels') },
            ],
        };
    }

    _bindInput() {
        this._gridTapHandler = (e) => {
            if (this._gameOver || this._levelComplete) return;
            const local = e.getLocalPosition(this._board.container);
            const col = Math.floor(local.x / this._grid.cellSize);
            const row = Math.floor(local.y / this._grid.cellSize);
            this._onGridTap(col, row);
        };

        const hitArea = this._board.getHitArea();
        hitArea.on('pointertap', this._gridTapHandler);

        this._onTap = () => {
            if (this._gameOver) this._restartGame();
        };
        this.game.input.on('tap', this._onTap);
    }

    // ── tile queue ─────────────────────────────────────

    _fillQueue() {
        while (this._queue.length < QUEUE_SIZE) {
            this._queue.push({
                type: this._randomTileType(),
                rotation: Math.floor(Math.random() * 4),
            });
        }
    }

    _randomTileType() {
        const entries = Object.entries(TILE_WEIGHTS);
        const total = entries.reduce((s, [, w]) => s + w, 0);
        let roll = Math.random() * total;
        for (const [type, weight] of entries) {
            roll -= weight;
            if (roll <= 0) return type;
        }
        return 'straight';
    }

    _renderQueue() {
        for (const g of this._queueGfx) {
            if (g.parent) g.parent.removeChild(g);
            g.destroy();
        }
        this._queueGfx = [];

        const previewSize = 50;
        const currentSize = 62;
        const gap = 8;
        const totalW = currentSize + gap +
            (this._queue.length - 1) * (previewSize + gap);
        let x = Math.floor((this.game.width - totalW) / 2);
        const y = 20;

        for (let i = 0; i < this._queue.length; i++) {
            const { type, rotation } = this._queue[i];
            const size = i === 0 ? currentSize : previewSize;
            const connections = rotateConnections(TILE_DEFS[type], rotation);

            const g = new Graphics();
            g.x = x;
            g.y = y;

            if (i === 0) {
                g.roundRect(-4, -4, size + 8, size + 8, 10);
                g.fill(0xfbbf24);
            }

            drawTileGraphics(g, size, connections, false, COLOR_TILE_BG);

            this._hudContainer.addChild(g);
            this._queueGfx.push(g);

            x += size + gap;
        }
    }

    // ── game actions ───────────────────────────────────

    _onGridTap(col, row) {
        const existingTile = this._board.getTile(col, row);
        if (existingTile) {
            if (!existingTile.passed) {
                this._board.rotateTile(col, row);
            }
            return;
        }

        if (this._queue.length === 0) return;
        if (!this._board.isEmpty(col, row)) return;

        const { type, rotation } = this._queue.shift();
        this._board.placeTile(col, row, type, rotation);

        this._fillQueue();
        this._renderQueue();
    }

    _startTrain() {
        if (this._trainStarted) return;
        const ok = this._train.start(this._board);
        if (!ok) {
            this._setGameOver();
            return;
        }
        this._trainStarted = true;
    }

    _onLevelComplete() {
        this._levelComplete = true;
        this._hudContainer.visible = false;
        this._train.score += END_BONUS;

        partyMode();

        const sVisited = this._train.stationsVisited;
        const sTotal = this._totalStations;
        const stationBonus = sVisited * STATION_POINTS;
        const levelScore = this._train.score;
        const totalScore = this._totalScore + levelScore;

        this._overlay = new LevelCompleteOverlay();
        this._overlay.show(this.game.width, this.game.height, {
            title: t('pipeline.level_complete', { v: this._level }),
            lines: [
                t('pipeline.tiles_passed', { v: this._train.tilesPassed }),
                t('pipeline.stations', { s: sVisited, t: sTotal, bonus: stationBonus }),
                t('pipeline.end_bonus', { v: END_BONUS }),
                t('pipeline.level_score', { v: levelScore }),
            ],
            score: totalScore,
            scoreLabel: t('common.total_score'),
            onContinue: () => this._showScoreOverlay(totalScore, levelScore, sVisited, sTotal),
        });
        this.container.addChild(this._overlay.container);
    }

    _showScoreOverlay(totalScore, levelScore, sVisited, sTotal) {
        if (this._overlay) { this._overlay.destroy(); this._overlay = null; }
        this._scoreOverlay = new ScoreSubmitOverlay();
        this._scoreOverlay.show({
            game: 'pipeline',
            score: totalScore,
            outcome: 'win',
            meta: {
                level: this._level,
                levelScore,
                tilesPassed: this._train.tilesPassed,
                stationsVisited: sVisited,
                stationsTotal: sTotal,
            },
            theme: this.game.theme,
            onDone: () => {
                this._scoreOverlay = null;
                this._nextLevel();
            },
        });
    }

    _nextLevel() {
        this._totalScore += this._train.score;
        this._level++;

        if (this._overlay) { this._overlay.destroy(); this._overlay = null; }

        const hitArea = this._board.getHitArea();
        if (hitArea && this._gridTapHandler) hitArea.off('pointertap', this._gridTapHandler);

        if (this._board) this._board.destroy();
        if (this._train) this._train.destroy();
        for (const g of this._queueGfx) {
            if (g.parent) g.parent.removeChild(g);
            g.destroy();
        }
        this._queueGfx = [];
        this._worldContainer.removeChildren();

        this._createBoard();
        this._createTrain();
        this._queue = [];
        this._fillQueue();
        this._renderQueue();
        this._bindGridOnly();

        this._trainStarted = false;
        this._countdownTimer = 0;
        this._levelComplete = false;
        this._gameOver = false;
        this._hudContainer.visible = true;
        this._messageText.visible = false;
        this._updateHud();
    }

    _bindGridOnly() {
        this._gridTapHandler = (e) => {
            if (this._gameOver || this._levelComplete) return;
            const local = e.getLocalPosition(this._board.container);
            const col = Math.floor(local.x / this._grid.cellSize);
            const row = Math.floor(local.y / this._grid.cellSize);
            this._onGridTap(col, row);
        };
        const hitArea = this._board.getHitArea();
        hitArea.on('pointertap', this._gridTapHandler);
    }

    _setGameOver() {
        this._gameOver = true;
        const sVisited = this._train.stationsVisited;
        const sTotal = this._totalStations;
        this._messageText.visible = false;
        this._hudContainer.visible = false;

        const message = t('pipeline.game_over', {
            level: this._level,
            tiles: this._train.tilesPassed,
            s: sVisited,
            t: sTotal,
            score: this._totalScore + this._train.score,
        });
        const lines = message.split('\n').filter(Boolean);
        const title = lines.shift() || t('common.game_over');
        lines.pop();

        this._gameOverOverlay?.destroy();
        this._gameOverOverlay = new GameOverOverlay();
        this._gameOverOverlay.show(this.game.width, this.game.height, { title, lines });
        this.container.addChild(this._gameOverOverlay.container);
    }

    _restartGame() {
        this._level = 1;
        this._totalScore = 0;
        this.destroy();
        this.container.removeChildren();
        this.init(this.game);
    }

    _updateHud() {
        const sVisited = this._train.stationsVisited;
        const sTotal = this._totalStations;
        this._hudText.text = t('pipeline.hud', {
            level: this._level,
            score: this._totalScore + this._train.score,
            tiles: this._train.tilesPassed,
            s: sVisited,
            t: sTotal,
        });
    }
}
